import { Controller } from "@hotwired/stimulus";
import { smartMatch } from "../../utils/smart_search";

/**
 * Inventory Form Controller
 * Manages inventory form interactions and location selection
 */
export default class extends Controller {
  static targets = [
    "locationsList",
    "locationCheckbox",
    "selectedCount",
    "selectAllButton",
    "filterInput",
    "modeSelect",
    "locationsError",
  ];

  connect() {
    this.updateSelectedCount();
    this.updateModeUI();
  }

  /**
   * Updates the count of selected locations
   */
  updateSelectedCount() {
    if (this.hasSelectedCountTarget) {
      const count = this.selectedLocations.length;
      this.selectedCountTarget.textContent = count;
    }
    if (this.selectedLocations.length > 0) this.toggleLocationsError(false);
  }

  /**
   * Toggles the selection of a location
   */
  toggleLocation() {
    this.updateSelectedCount();
  }

  /**
   * Toggles selection of all locations
   */
  toggleSelectAll() {
    const isChecked = this.selectAllButtonTarget.checked;

    this.locationCheckboxTargets.forEach((checkbox) => {
      if (
        !checkbox.disabled &&
        checkbox.closest("tr").style.display !== "none"
      ) {
        checkbox.checked = isChecked;
      }
    });

    this.updateSelectedCount();
  }

  /**
   * Filters the locations list by name
   */
  filterLocations() {
    const filterValue = this.filterInputTarget.value;

    this.locationsListTarget.querySelectorAll("tr").forEach((row) => {
      const locationName = row.querySelector("label").textContent;
      row.style.display = smartMatch(locationName, filterValue) ? "" : "none";
    });
  }

  /**
   * Updates UI based on selected inventory mode
   */
  updateModeUI() {
    if (!this.hasModeSelectTarget) return;

    const mode = this.modeSelectTarget.value;
    const partialNote = document.getElementById("partial-inventory-note");
    const completeNote = document.getElementById("complete-inventory-note");

    if (partialNote && completeNote) {
      partialNote.classList.toggle("hidden", mode !== "partial");
      completeNote.classList.toggle("hidden", mode !== "complete");
    }
  }

  /**
   * Validates the form before submission
   * @param {Event} event - The form submit event
   */
  validateBeforeSubmit(event) {
    if (this.selectedLocations.length === 0) {
      event.preventDefault();
      this.toggleLocationsError(true);
      return false;
    }
    return true;
  }

  /**
   * Shows or hides the message asking for a location, on the form itself
   * rather than in a native alert() (QA PY-SP-27)
   * @param {boolean} shown
   */
  toggleLocationsError(shown) {
    if (!this.hasLocationsErrorTarget) return;

    this.locationsErrorTarget.classList.toggle("hidden", !shown);
    if (shown) this.locationsErrorTarget.scrollIntoView({ block: "center", behavior: "smooth" });
  }

  /**
   * Returns the array of selected location checkboxes
   */
  get selectedLocations() {
    return this.locationCheckboxTargets.filter((checkbox) => checkbox.checked);
  }
}
